import React, { useState } from 'react';
import { useFormik } from 'formik';
import { TextField, Select, MenuItem, FormControl, InputLabel, FormHelperText, Box, Typography } from '@mui/material';
import { Step2Schema } from '../../validations/user';
import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';
import BorderedButton from '../common/buttons/BorderedButton';
import { useNavigate } from 'react-router-dom';
import { validatePhoneNumber, validateZipcode } from '../../utils/validations';
import { addIntCodeToPhoneNumber } from '../../utils/user';
import useSnackBarAlertMessages from '../../hooks/useSnackBarAlertMessages';
import validateAddress from '../../utils/validations';
import useBackendApi from '../../hooks/useBackendApi';
import { Address } from '../../types/orderTypes';
import SearchableDropdown from '../common/SearchableDropdown';
import { US_STATES } from '../../constants/address';
import CustomDatePicker from '../../CustomDatePicker';
import dayjs from 'dayjs';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';

const app_co = process.env.REACT_APP_COMPANY;

type ProfileInfoValues = {
  phone_number: string;
  birthdate: string;
  gender: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  zipcode: string;
  // Allow for additional fields
  [key: string]: string;
};

interface Step2Props {
  onSubmit: (values: any, { setSubmitting }: any) => void;
  isSubmitting: boolean;
  initialFormValues?: Partial<ProfileInfoValues>;
  disabledFields?: (keyof ProfileInfoValues)[];
  btSubmitText?: string;
  btnCancelText?: string;
}

const SignupStep2ProfileInfo: React.FC<Step2Props> = ({
  onSubmit,
  isSubmitting,
  initialFormValues = {},
  disabledFields = [],
  btSubmitText = 'Next',
  btnCancelText = 'Cancel',
}) => {
  const { theme } = useThemeContext();
  const navigate = useNavigate();
  const [isValidatingInfo, setIsValidatingInfo] = useState<boolean>(false);
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();
  const { apiFetchRequest } = useBackendApi();

  const getInitialZipcode = () => {
    const invalidZipcode = initialFormValues.zipcode && validateZipcode(initialFormValues.zipcode);
    // if the initial zipcode is invalid(coming from Shopify), return an empty string
    // and force the user to enter a valid one since the beginning
    return invalidZipcode ? '' : initialFormValues.zipcode;
  };

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      phoneNumber: addIntCodeToPhoneNumber(initialFormValues.phone_number || ''),
      birthdate: initialFormValues.birthdate || '',
      gender: initialFormValues.gender || '',
      address_1: initialFormValues.address_1 || '',
      address_2: initialFormValues.address_2 || '',
      city: initialFormValues.city || '',
      state: initialFormValues.state || '',
      zipcode: getInitialZipcode() || '',
      occupation: initialFormValues.occupation || '',
    },
    validate: (values) => {
      const errors = {} as any;
      if (app_co === 'rh') {
        const zipcodeError = validateZipcode(values.zipcode);
        if (zipcodeError) {
          errors.zipcode = zipcodeError;
        }
        return errors;
      }
    },
    validationSchema: Step2Schema,
    onSubmit: onSubmit,
  });

  const handleNextStep = async () => {
    setIsValidatingInfo(true);

    const address: Address = {
      address1: formik.values.address_1,
      address2: formik.values.address_2,
      city: formik.values.city,
      state: formik.values.state,
      zipcode: formik.values.zipcode,
    };

    // Parallel validation for phone number, address, and date of birth
    const [isPhoneValid, isAddressValid] = await Promise.all([
      validatePhoneNumber(formik.values.phoneNumber),
      app_co === 'rh' ? validateAddress(address, apiFetchRequest) : Promise.resolve('VALID'),
    ]);

    setIsValidatingInfo(false);

    if (!isPhoneValid) {
      formik.setFieldError('phoneNumber', 'Enter a valid US Phone number');
      formik.setFieldTouched('phoneNumber', true, false);
      return;
    }
    else {
      formik.values.phoneNumber = addIntCodeToPhoneNumber(formik.values.phoneNumber);
    }

    if (isAddressValid !== 'VALID') {
      setAddressErrors();
      addAlertMessages({ message: 'Invalid Address. Please provide the correct address', severityType: 'error' });
      return;
    }

    // If all validations are successful
    if (isPhoneValid && isAddressValid === 'VALID') {
      formik.setFieldError('phoneNumber', undefined);
      formik.setFieldError('birthdate', undefined);
      clearAddressErrors();
      formik.handleSubmit();
      return;
    }
  };

  // Helper function to clear address errors
  const clearAddressErrors = () => {
    formik.setFieldError('address_1', undefined);
    formik.setFieldError('address_2', undefined);
    formik.setFieldError('city', undefined);
    formik.setFieldError('state', undefined);
    formik.setFieldError('zipcode', undefined);
  };

  // Helper function to set address errors
  const setAddressErrors = () => {
    formik.setFieldTouched('address_1', true, false);
    formik.setFieldTouched('address_2', true, false);
    formik.setFieldTouched('city', true, false);
    formik.setFieldTouched('state', true, false);
    formik.setFieldTouched('zipcode', true, false);
  };

  return (
    <Box
      sx={{
        mt: 1,
        mb: 3,
        padding: { xs: '0 24px', sm: '0 24px', md: '0' },
      }}
    >
      <Typography variant="h4" sx={{ color: theme.custom.components.signupStep1AccountDetails.typography.color }}>
        Complete your profile info
      </Typography>
      <Typography
        variant="body2"
        sx={{
          margin: { md: '4px 0 17px 0' },
          color: theme.custom.components.signupStep1AccountDetails.typography.color,
        }}
      >
        Please complete your personal information.
      </Typography>
      <Box
        sx={{
          [theme.breakpoints.up('md')]: {
            display: 'flex',
            flexWrap: 'wrap',
          },
        }}
      >
        <TextField
          sx={{
            [theme.breakpoints.up('md')]: {
              margin: '10px 10px 10px 0',
              width: '31%',
            },
          }}
          fullWidth
          margin="normal"
          required
          label="Phone Number"
          {...formik.getFieldProps('phoneNumber')}
          error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
          helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
        />
        <CustomDatePicker
          DatePickerProps={{ disabled: disabledFields.includes('birthdate') }}
          TextFieldProps={{
            shrink: 'true',
            sx: {
              [theme.breakpoints.up('md')]: {
                margin: '10px 10px 10px 0',
                width: '31%',
              },
            },
          }}
          value={formik.values.birthdate ? dayjs(formik.values.birthdate) : null}
          onChange={(newValue: string | null) => {
            if (newValue === null) {
              formik.setFieldTouched('birthdate', true);
              formik.setFieldValue('birthdate', '', true);
              formik.setFieldError('birthdate', '');
            } else {
              formik.setFieldTouched('birthdate', true);
              formik.setFieldValue('birthdate', newValue, true);
            }
          }}
          label="Birthdate"
          error={formik.touched.birthdate && Boolean(formik.errors.birthdate)}
          helperText={formik.touched.birthdate && formik.errors.birthdate ? formik.errors.birthdate : ''}
        />
        <FormControl
          sx={{
            [theme.breakpoints.up('md')]: {
              margin: '10px 10px 10px 0',
              width: '31%',
            },
          }}
          fullWidth
          required
          margin="normal"
          error={formik.touched.gender && Boolean(formik.errors.gender)}
        >
          <InputLabel id="gender-label">Gender at birth</InputLabel>
          <Select
            labelId="gender-label"
            label="Gender"
            {...formik.getFieldProps('gender')}
            value={formik.values.gender}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="unknown">Unknown</MenuItem>
          </Select>
          {formik.touched.gender && formik.errors.gender && <FormHelperText>{formik.errors.gender}</FormHelperText>}
        </FormControl>
        {app_co === 'rh' && (
          <>
            <TextField
              sx={{
                [theme.breakpoints.up('md')]: {
                  margin: '10px 10px 10px 0',
                  width: '47.5%',
                },
              }}
              fullWidth
              required
              margin="normal"
              label="Address"
              {...formik.getFieldProps('address_1')}
              error={formik.touched.address_1 && Boolean(formik.errors.address_1)}
              helperText={formik.touched.address_1 && formik.errors.address_1}
            />
            <TextField
              sx={{
                [theme.breakpoints.up('md')]: {
                  margin: '10px 10px 10px 0',
                  width: '47%',
                },
              }}
              fullWidth
              margin="normal"
              label="Address 2"
              {...formik.getFieldProps('address_2')}
              error={formik.touched.address_2 && Boolean(formik.errors.address_2)}
              helperText={formik.touched.address_2 && formik.errors.address_2}
            />
            <TextField
              sx={{
                [theme.breakpoints.up('md')]: {
                  margin: '10px 10px 10px 0',
                  width: '48%',
                },
              }}
              fullWidth
              required
              margin="normal"
              label="City"
              {...formik.getFieldProps('city')}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
            />
            <SearchableDropdown
              label="State"
              options={US_STATES}
              {...formik.getFieldProps('state')}
              onChange={(selectedOption: { value: any }) => {
                formik.setFieldValue('state', selectedOption?.value || '');
              }}
              sx={{
                [theme.breakpoints.up('md')]: {
                  margin: '10px 10px 10px 0',
                  width: '23%',
                },
              }}
              error={formik.touched.state && Boolean(formik.errors.state)}
              helperText={formik.touched.state && formik.errors.state}
              disabled={false}
              value={formik.values.state}
            />
            <TextField
              sx={{
                [theme.breakpoints.up('md')]: {
                  margin: '10px 10px 10px 0',
                  width: '22%',
                },
              }}
              fullWidth
              required
              margin="normal"
              label="Zip Code"
              {...formik.getFieldProps('zipcode')}
              error={formik.touched.zipcode && Boolean(formik.errors.zipcode)}
              helperText={formik.touched.zipcode && formik.errors.zipcode}
            />
            <FormControl
              sx={{
                [theme.breakpoints.up('md')]: {
                  margin: '10px 10px 10px 0',
                  width: '31%',
                },
              }}
              fullWidth
              required
              margin="normal"
              error={formik.touched.occupation && Boolean(formik.errors.occupation)}
            >
              <InputLabel id="occupation-label">Occupation</InputLabel>
              <Select labelId="occupation-label" label="Occupation" {...formik.getFieldProps('occupation')}>
                <MenuItem value="Agriculture">Agriculture</MenuItem>
                <MenuItem value="Automotive industry or Automotive Manufacturing">
                  Automotive industry or Automotive Manufacturing
                </MenuItem>
                <MenuItem value="Chemical industry">Chemical industry</MenuItem>
                <MenuItem value="Construction">Construction</MenuItem>
                <MenuItem value="Education">Education</MenuItem>
                <MenuItem value="First responder (fire, EMS, police)">First responder (fire, EMS, police)</MenuItem>
                <MenuItem value="Healthcare">Healthcare</MenuItem>
                <MenuItem value="Office worker">Office worker</MenuItem>
                <MenuItem value="Sports industry (including winter sports)">
                  Sports industry (including winter sports)
                </MenuItem>
                <MenuItem value="Textile or carpet industry">Textile or carpet industry</MenuItem>
                <MenuItem value="Other not listed">Other not listed</MenuItem>
              </Select>
              {formik.touched.occupation && formik.errors.occupation && (
                <FormHelperText>{formik.errors.occupation}</FormHelperText>
              )}
            </FormControl>
          </>
        )}
      </Box>
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'center', sm: 'flex-start' },
          maxWidth: { xs: '100%', sm: 'auto' },
          padding: { xs: '0 24px', sm: '0' },
        }}
      >
        <PrimaryBlueButton
          isLoading={isSubmitting || isValidatingInfo}
          onClick={handleNextStep}
          buttonText={btSubmitText}
          sxProps={{
            marginBottom: { xs: '10px', sm: '10px' },
          }}
          additionalProps={{
            disabled: !formik.isValid || isSubmitting || isValidatingInfo,
          }}
        />
        <BorderedButton
          sxProps={{
            marginLeft: { xs: '0', sm: '10px' },
          }}
          additionalProps={{
            style: { height: '41px' },
          }}
          onClick={() => navigate('/')}
          buttonText={btnCancelText}
        />
      </Box>
      {renderSnackBarAlertComponent()}
    </Box>
  );
};

export default SignupStep2ProfileInfo;
